import { useCallback, useEffect, useRef } from 'react'

/**
 * 400ms, the same figure the platform's filter drawer debounces at
 * (`apps/platform/src/components/input/filters/Filters.tsx`, `AUTO_APPLY_MS`).
 * Mirrored rather than re-derived: the two apps' filter surfaces now share a
 * commit MODEL, so they should not disagree on its one tunable.
 */
export const AUTO_APPLY_MS = 400

/**
 * The commit half of auto-apply: no Apply button, every change commits itself.
 *
 * Returns a `schedule()` that a field handler calls after staging its change.
 * The commit runs `delay` ms later, and a second `schedule()` inside that window
 * replaces the first — so ticking three status pills in a row costs ONE query,
 * not three. That coalescing is the entire reason a debounce is mandatory here
 * rather than optional: without it, removing Apply converts every multi-facet
 * edit into a burst of redundant requests.
 *
 * ## Triggered by INTERACTION, not by watching state
 *
 * The platform's drawer watches its staged array in an effect and skips a commit
 * when the array matches what it last sent. That works there because the drawer
 * owns one array. These sheets own five to eight separate `useState`s each, and
 * every one of them is RE-SEEDED from the applied values when the sheet opens
 * (see the `[open]` effects in `ActivityFilterDrawer`, `ScansFilterDrawer`,
 * `SortDrawer`) — so a state-watching effect would see the re-seed as a change
 * and bounce the applied values straight back to the parent on every open.
 *
 * Hanging the schedule off the interaction removes that class of bug outright:
 * a re-seed is not an interaction, so it never schedules anything. It also means
 * no deep-equality machinery and no per-sheet payload comparator.
 *
 * The commit callback is read from a ref at fire time, so it closes over the
 * LATEST render's state rather than the state at the moment of the tap. That is
 * load-bearing: `schedule()` is called from an event handler whose own closure
 * still holds the pre-update values.
 *
 * ## A pending commit is FLUSHED, never dropped
 *
 * Auto-apply has no undo, so a change the user has already made must land. Tap a
 * status pill and dismiss the sheet within 400ms and the naive debounce loses
 * the change entirely — the sheet says the filter is set, the list disagrees,
 * and there is no Apply left to reconcile them. So a pending commit is flushed
 * on the close transition AND on unmount (`CertificatesFilter` unmounts with its
 * drawer; the sub-view sheets stay mounted and only toggle `open`, so both paths
 * are needed).
 *
 * @param commit fires the actual apply. Re-created per render; always read fresh.
 * @param open   the sheet's open state. Falling to `false` flushes.
 */
export function useAutoApply(commit: () => void, open = true, delay = AUTO_APPLY_MS) {
  const commitRef = useRef(commit)
  useEffect(() => {
    commitRef.current = commit
  })

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = useCallback(() => {
    if (timer.current === null) return
    clearTimeout(timer.current)
    timer.current = null
    commitRef.current()
  }, [])

  const schedule = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      commitRef.current()
    }, delay)
  }, [delay])

  // Closing lands anything pending. No-op on the first render of a closed sheet,
  // because nothing has been scheduled yet.
  useEffect(() => {
    if (open) return
    flush()
  }, [open, flush])

  // Same guarantee for a sheet that is unmounted rather than closed.
  useEffect(() => () => flush(), [flush])

  return { schedule, flush }
}

export default useAutoApply
